import React, { useState, useEffect } from 'react';
import axios from '../config/axios';

/**
 * QuestManager - Parent component for creating and managing weekly quests
 */
const QuestManager = ({ householdKids, onQuestCreated }) => {
    const [templates, setTemplates] = useState([]);
    const [quests, setQuests] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [targetType, setTargetType] = useState('chores_completed');
    const [targetValue, setTargetValue] = useState('5');
    const [bonusReward, setBonusReward] = useState('5');
    const [emoji, setEmoji] = useState('🎯');
    const [selectedKids, setSelectedKids] = useState([]);
    const [autoRenew, setAutoRenew] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTemplates();
        fetchQuests();
    }, []);

    const fetchTemplates = async () => {
        try {
            const response = await axios.get('/api/v1/quests/templates');
            setTemplates(response.data || []);
        } catch (err) {
            console.error('Failed to fetch templates:', err);
        }
    };

    const fetchQuests = async () => {
        try {
            const response = await axios.get('/api/v1/quests');
            setQuests(response.data || []);
        } catch (err) {
            console.error('Failed to fetch quests:', err);
        }
    };

    const handleTemplateSelect = (template) => {
        setTitle(template.title);
        setTargetType(template.target_type);
        setTargetValue(template.target_value.toString());
        setBonusReward((template.bonus_reward / 100).toString());
        setEmoji(template.emoji);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post('/api/v1/quests', {
                title,
                target_type: targetType,
                target_value: parseInt(targetValue),
                bonus_reward: Math.round(parseFloat(bonusReward) * 100),
                emoji,
                kid_user_ids: selectedKids.length > 0 ? selectedKids : undefined,
                auto_renew: autoRenew
            });

            setShowCreateModal(false);
            resetForm();
            fetchQuests();
            if (onQuestCreated) onQuestCreated();
        } catch (err) {
            console.error('Failed to create quest:', err);
            setError(err.response?.data || 'Failed to create quest');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setTitle('');
        setTargetType('chores_completed');
        setTargetValue('5');
        setBonusReward('5');
        setEmoji('🎯');
        setSelectedKids([]);
        setAutoRenew(false);
        setError('');
    };

    const toggleKidSelection = (kidId) => {
        setSelectedKids(prev =>
            prev.includes(kidId)
                ? prev.filter(id => id !== kidId)
                : [...prev, kidId]
        );
    };

    const emojiOptions = ['🎯', '🏆', '🥇', '💰', '🔥', '⭐', '🚀', '💪'];

    return (
        <div style={{ marginBottom: '32px' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
            }}>
                <h2 style={{
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    🎯 Weekly Quests
                </h2>
                <button
                    onClick={() => setShowCreateModal(true)}
                    aria-label="Create a new weekly quest"
                    style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem'
                    }}
                >
                    + Create Quest
                </button>
            </div>

            {/* Active Quests */}
            {quests.length > 0 ? (
                <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                    {quests.map(quest => (
                        <div key={quest.ID} style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                <span style={{ fontSize: '32px' }}>{quest.emoji}</span>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>{quest.title}</h3>
                                    <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
                                        Bonus: ${(quest.bonus_reward / 100).toFixed(2)}
                                    </p>
                                </div>
                            </div>

                            {/* Progress by kid */}
                            {quest.assignees?.map(assignee => (
                                <div key={assignee.kid_user_id} style={{
                                    padding: '12px',
                                    background: '#f9fafb',
                                    borderRadius: '8px',
                                    marginTop: '8px'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontWeight: '600' }}>
                                            {assignee.kid_avatar} {assignee.kid_name}
                                        </span>
                                        <span style={{
                                            color: assignee.is_complete ? '#10b981' : '#6b7280',
                                            fontWeight: '600'
                                        }}>
                                            {assignee.is_complete ? '✅ Complete' : `${assignee.percent_complete}%`}
                                        </span>
                                    </div>
                                    <div style={{
                                        background: '#e5e7eb',
                                        borderRadius: '4px',
                                        height: '8px',
                                        overflow: 'hidden'
                                    }} role="progressbar" aria-valuenow={assignee.percent_complete} aria-valuemin={0} aria-valuemax={100} aria-label={`${assignee.kid_name} quest progress: ${assignee.percent_complete}%`}>
                                        <div style={{
                                            background: assignee.is_complete ? '#10b981' : '#8b5cf6',
                                            width: `${assignee.percent_complete}%`,
                                            height: '100%'
                                        }} />
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '4px' }}>
                                        {assignee.current_progress} / {assignee.target_value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{
                    background: '#f9fafb',
                    borderRadius: '16px',
                    padding: '32px',
                    textAlign: 'center',
                    color: '#6b7280'
                }}>
                    <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>🎯</span>
                    <p style={{ margin: 0 }}>No active quests. Create one to motivate your kids!</p>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={() => setShowCreateModal(false)}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: '20px',
                            padding: '32px',
                            maxWidth: '500px',
                            width: '90%',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Create weekly quest"
                    >
                        <h2 style={{ margin: '0 0 20px', fontSize: '1.5rem' }}>🎯 Create Weekly Quest</h2>

                        {/* Quick Templates */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                Quick Templates
                            </label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {templates.map(t => (
                                    <button
                                        key={t.title}
                                        type="button"
                                        onClick={() => handleTemplateSelect(t)}
                                        style={{
                                            padding: '8px 12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                            background: title === t.title ? '#8b5cf6' : 'white',
                                            color: title === t.title ? 'white' : '#374151',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {t.emoji} {t.title}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Title */}
                            <div style={{ marginBottom: '16px' }}>
                                <label htmlFor="quest-title" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Quest Title
                                </label>
                                <input
                                    id="quest-title"
                                    type="text"
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    placeholder="e.g., Complete 5 Chores"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            {/* Emoji */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Icon
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {emojiOptions.map(e => (
                                        <button
                                            key={e}
                                            type="button"
                                            onClick={() => setEmoji(e)}
                                            aria-label={`Select emoji ${e}`}
                                            aria-pressed={emoji === e}
                                            style={{
                                                width: '44px',
                                                height: '44px',
                                                fontSize: '24px',
                                                borderRadius: '8px',
                                                border: emoji === e ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                                                background: emoji === e ? '#ede9fe' : 'white',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {e}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Target Type */}
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    Quest Type
                                </label>
                                <select
                                    value={targetType}
                                    onChange={e => setTargetType(e.target.value)}
                                    aria-label="Quest type"
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="chores_completed">Complete X Chores</option>
                                    <option value="total_earned">Earn X DadBucks</option>
                                    <option value="streak_days">Maintain X Day Streak</option>
                                </select>
                            </div>

                            {/* Target Value and Bonus */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        Target
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={targetValue}
                                        onChange={e => setTargetValue(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        Bonus Reward ($)
                                    </label>
                                    <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={bonusReward}
                                        onChange={e => setBonusReward(e.target.value)}
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Kid Selection */}
                            {householdKids && householdKids.length > 0 && (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                        Assign To (leave empty for all kids)
                                    </label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {householdKids.map(kid => (
                                            <button
                                                key={kid.user_id}
                                                type="button"
                                                onClick={() => toggleKidSelection(kid.user_id)}
                                                style={{
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    border: selectedKids.includes(kid.user_id)
                                                        ? '2px solid #8b5cf6'
                                                        : '1px solid #e5e7eb',
                                                    background: selectedKids.includes(kid.user_id)
                                                        ? '#ede9fe'
                                                        : 'white',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {kid.avatar || '😊'} {kid.display_name || kid.username}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Auto Renew */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={autoRenew}
                                        onChange={e => setAutoRenew(e.target.checked)}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <span style={{ fontWeight: '600' }}>Auto-renew each week</span>
                                </label>
                            </div>

                            {error && (
                                <p style={{ color: '#dc2626', marginBottom: '16px' }} role="alert">{error}</p>
                            )}

                            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    style={{
                                        padding: '12px 24px',
                                        borderRadius: '10px',
                                        border: '1px solid #e5e7eb',
                                        background: 'white',
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{
                                        padding: '12px 24px',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                        color: 'white',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        fontWeight: '600',
                                        opacity: loading ? 0.7 : 1
                                    }}
                                >
                                    {loading ? 'Creating...' : '🎯 Create Quest'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuestManager;
